/**
 * The real pages of a stored Office file, from the server's document engine.
 *
 * The same thing the agent's right panel shows, for a file the Library holds:
 * the .docx or .pptx itself laid out by LibreOffice and drawn page by page,
 * so the fonts, colours, letterhead and page breaks are the document's own.
 * The Library used to open a Word file as black-on-white text and a deck as
 * slide sheets — the same file the agent had just shown in full design
 * (operator report 2026-09-10).
 *
 * `onUnavailable` fires when this file has no page preview (an old format, no
 * engine, a conversion that failed). The caller then renders whatever it drew
 * before, so nobody is left with an empty pane.
 */

import React, { useEffect, useState } from 'react';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import PdfPagesView from '@/modules/conversations/components/artifacts/PdfPagesView';
import '@/modules/conversations/components/artifacts/ArtifactPanel.css';

interface Props {
  /** The stored Library/agent file to lay out. */
  fileId?: string;
  /** Or an exact endpoint that answers with the PDF — used for a version that
   *  is not stored yet, e.g. the one an edit card proposes. */
  url?: string;
  title?: string;
  /** Called once when the server says this file cannot be shown as pages. */
  onUnavailable?: () => void;
}

/** How long to keep asking while the engine is busy laying the file out. */
const MAX_ATTEMPTS = 20;
const RETRY_MS = 1500;

export const ExactPagesView: React.FC<Props> = ({ fileId, url, title, onUnavailable }) => {
  const { t } = useLanguage();
  const [pdf, setPdf] = useState<ArrayBuffer | null | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    let timer = 0;
    let attempts = 0;
    setPdf(undefined);
    const endpoint = url || `/files/preview-pages/${fileId}`;
    const ask = () => {
      attempts += 1;
      api.get(endpoint, { responseType: 'arraybuffer' })
        .then((res) => { if (!cancelled) setPdf(res.data as ArrayBuffer); })
        .catch((err) => {
          if (cancelled) return;
          const status = err?.response?.status;
          // A busy engine (503) or a transient gateway error is worth waiting
          // out; 415 means this format has no pages and never will.
          if ((status === 503 || status === 502 || !status) && attempts < MAX_ATTEMPTS) {
            timer = window.setTimeout(ask, RETRY_MS);
            return;
          }
          setPdf(null);
          onUnavailable?.();
        });
    };
    ask();
    return () => { cancelled = true; window.clearTimeout(timer); };
    // onUnavailable is a notification, not an input: re-running on a new
    // closure identity would restart the fetch on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId, url]);

  if (pdf) {
    return <PdfPagesView data={pdf} title={title} />;
  }
  if (pdf === undefined) {
    // Pages-to-be, in the same gutter they will occupy — never a blank pane
    // and never a look-alike that jumps to the real thing seconds later.
    return (
      <div className="artifact-pdf artifact-pdf--pending" role="status" aria-live="polite">
        <div className="artifact-pdf__pages">
          <div className="artifact-pdf-page-wrap artifact-pdf-skeleton" />
          <div className="artifact-pdf-page-wrap artifact-pdf-skeleton artifact-pdf-skeleton--second" />
        </div>
        <div className="artifact-pdf__count">{t('chat.artifacts.preparingExact')}</div>
      </div>
    );
  }
  return null;
};

export default ExactPagesView;
